import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { FaUserCheck, FaClipboardList, FaCrown, FaUserFriends, FaMoneyBillWave, FaChartPie } from 'react-icons/fa';
import { db } from '../../services/firebaseConnection';

const PRIMARY_COLOR = '#ec8e00';
const GRID_COLOR = '#2e2e2e';

const formatCurrency = (value = 0) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });

const parseAmount = (appointment) => {
  const candidates = [
    appointment?.pagamento?.valor?.original,
    appointment?.pagamento?.valor?.originalValue,
    appointment?.pagamento?.valor,
    appointment?.valor,
    appointment?.total,
    appointment?.totalAmount,
    appointment?.amount,
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const parsed = parseFloat(String(candidate).replace(',', '.'));
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return 0;
};

const normalizeServices = (services) => {
  if (!services) return [];
  if (Array.isArray(services)) return services.filter(Boolean).map((service) => service.toString());
  if (typeof services === 'string') return services.split(',').map((item) => item.trim()).filter(Boolean);
  return [];
};

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

const monthLabel = (monthKey) => {
  const [year, month] = monthKey.split('-').map((v) => parseInt(v, 10));
  if (!year || !month) return monthKey;
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
};

const resolveUserName = (userId, userName, directory) => {
  if (directory?.has(userId)) {
    return directory.get(userId)?.name || userName || 'Cliente';
  }
  return userName || 'Cliente';
};

export default function DashboardClientes({ users = [] }) {
  const [rawAppointments, setRawAppointments] = useState([]);
  const [metrics, setMetrics] = useState({
    activeClients: 0,
    totalAppointments: 0,
    topSpender: null,
    topVisitor: null,
    totalRevenue: 0,
  });
  const [visitRanking, setVisitRanking] = useState([]);
  const [spendRanking, setSpendRanking] = useState([]);
  const [charts, setCharts] = useState({
    monthlyAppointments: [],
    monthlyRevenue: [],
    serviceBreakdown: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const userDirectory = useMemo(() => {
    const directory = new Map();
    users.forEach((user) => {
      if (user?.id) {
        directory.set(user.id, user);
      }
    });
    return directory;
  }, [users]);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setError('');
      try {
        const appointmentsRef = collection(db, 'agendamentos');
        const snapshot = await getDocs(appointmentsRef);
        const confirmed = [];

        snapshot.forEach((docSnap) => {
          const userId = docSnap.id;
          const data = docSnap.data() || {};
          Object.entries(data).forEach(([dateKey, items]) => {
            if (!Array.isArray(items)) return;
            items.forEach((appointment) => {
              if (normalizeStatus(appointment?.paymentStatus) !== 'confirmado') return;
              confirmed.push({ ...appointment, date: appointment?.date || appointment?.data || dateKey, userId });
            });
          });
        });
        setRawAppointments(confirmed);
      } catch (err) {
        console.error('Erro ao carregar dashboard de clientes:', err);
        setError('Não foi possível carregar os dados dos clientes.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const processedData = useMemo(() => {
    if (!rawAppointments.length) {
      return {
        metrics: {
          activeClients: 0,
          totalAppointments: 0,
          topSpender: null,
          topVisitor: null,
          totalRevenue: 0,
        },
        visitRanking: [],
        spendRanking: [],
        charts: {
          monthlyAppointments: [],
          monthlyRevenue: [],
          serviceBreakdown: [],
        },
      };
    }

    const clientStats = new Map();
    const monthlyAppointments = new Map();
    const monthlyRevenue = new Map();
    const serviceUsage = new Map();

    rawAppointments.forEach((appointment) => {
      const amount = parseAmount(appointment);
      const dateKey = appointment?.date || appointment?.data;
      const monthKey = (() => {
        if (!dateKey) return '';
        const [year, month] = dateKey.split('-');
        if (year && month) {
          return `${year}-${month.padStart(2, '0')}`;
        }
        return '';
      })();

      if (monthKey) {
        monthlyAppointments.set(monthKey, (monthlyAppointments.get(monthKey) || 0) + 1);
        monthlyRevenue.set(monthKey, (monthlyRevenue.get(monthKey) || 0) + amount);
      }

      const services = normalizeServices(appointment?.services);
      services.forEach((service) => {
        serviceUsage.set(service, (serviceUsage.get(service) || 0) + 1);
      });

      const clientId = appointment.userId || appointment.userName || 'desconhecido';
      const existing = clientStats.get(clientId) || {
        name: resolveUserName(clientId, appointment.userName, userDirectory),
        visits: 0,
        totalSpent: 0,
        services: new Map(),
        professionals: new Map(),
        lastVisit: null,
      };

      existing.visits += 1;
      existing.totalSpent += amount;
      if (services.length) {
        services.forEach((service) => {
          existing.services.set(service, (existing.services.get(service) || 0) + 1);
        });
      }
      if (appointment.professional) {
        existing.professionals.set(
          appointment.professional,
          (existing.professionals.get(appointment.professional) || 0) + 1,
        );
      }
      if (dateKey) {
        const parsedDate = new Date(dateKey);
        if (!Number.isNaN(parsedDate)) {
          existing.lastVisit = existing.lastVisit ? new Date(Math.max(existing.lastVisit, parsedDate)) : parsedDate;
        }
      }
      clientStats.set(clientId, existing);
    });

    const statsArray = Array.from(clientStats.entries()).map(([clientId, info]) => ({
      id: clientId,
      ...info,
    }));

    const visitRanking = statsArray
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10)
      .map((entry) => {
        const topProfessional = Array.from(entry.professionals.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
        return {
          id: entry.id,
          name: entry.name,
          visits: entry.visits,
          topProfessional,
          lastVisit: entry.lastVisit ? entry.lastVisit.toLocaleDateString('pt-BR') : '-',
        };
      });

    const spendRanking = statsArray
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 10)
      .map((entry) => {
        const topService = Array.from(entry.services.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
        const averageTicket = entry.visits ? entry.totalSpent / entry.visits : 0;
        return {
          id: entry.id,
          name: entry.name,
          totalSpent: entry.totalSpent,
          averageTicket,
          topService,
        };
      });

    const topVisitor = visitRanking[0] || null;
    const topSpender = spendRanking[0] || null;

    const monthlyAppointmentsData = Array.from(monthlyAppointments.entries())
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([monthKey, total]) => ({ month: monthLabel(monthKey), total }));

    const monthlyRevenueData = Array.from(monthlyRevenue.entries())
      .sort(([a], [b]) => (a > b ? 1 : -1))
      .map(([monthKey, total]) => ({ month: monthLabel(monthKey), total }));

    const serviceBreakdown = Array.from(serviceUsage.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([service, total]) => ({ name: service, value: total }));

    return {
      metrics: {
        activeClients: clientStats.size,
        totalAppointments: rawAppointments.length,
        topSpender,
        topVisitor,
        totalRevenue: spendRanking.reduce((acc, entry) => acc + entry.totalSpent, 0),
      },
      visitRanking,
      spendRanking,
      charts: {
        monthlyAppointments: monthlyAppointmentsData,
        monthlyRevenue: monthlyRevenueData,
        serviceBreakdown,
      },
    };
  }, [rawAppointments, userDirectory]);

  useEffect(() => {
    setMetrics(processedData.metrics);
    setVisitRanking(processedData.visitRanking);
    setSpendRanking(processedData.spendRanking);
    setCharts(processedData.charts);
  }, [processedData]);

  return (
    <div className="clients-dashboard premium-panel">
      <div className="dashboard-header">
        <div>
          <p className="eyebrow">Inteligência de clientes</p>
          <h2>Clientes Dashboard</h2>
          <p className="subtitle">Visão consolidada de visitas, faturamento e serviços confirmados.</p>
        </div>
        <div className="header-chip">
          <FaChartPie />
          <span>Dados em tempo real</span>
        </div>
      </div>

      {error && <div className="alert-error">{error}</div>}

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-icon primary"><FaUserFriends /></div>
          <div>
            <p className="stat-label">Clientes ativos</p>
            <h3>{metrics.activeClients}</h3>
            <span>Clientes com agendamentos confirmados</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon accent"><FaClipboardList /></div>
          <div>
            <p className="stat-label">Total de agendamentos</p>
            <h3>{metrics.totalAppointments}</h3>
            <span>Registros confirmados</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon highlight"><FaCrown /></div>
          <div>
            <p className="stat-label">Cliente que mais gastou</p>
            <h3>{metrics.topSpender?.name || '-'}</h3>
            <span>{metrics.topSpender ? formatCurrency(metrics.topSpender.totalSpent) : 'Sem dados'}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon premium"><FaUserCheck /></div>
          <div>
            <p className="stat-label">Cliente mais frequente</p>
            <h3>{metrics.topVisitor?.name || '-'}</h3>
            <span>{metrics.topVisitor ? `${metrics.topVisitor.visits} visitas` : 'Sem dados'}</span>
          </div>
        </div>
        <div className="stat-card full">
          <div className="stat-icon money"><FaMoneyBillWave /></div>
          <div>
            <p className="stat-label">Total faturado</p>
            <h3>{formatCurrency(metrics.totalRevenue)}</h3>
            <span>Pagamentos confirmados</span>
          </div>
        </div>
      </div>

      <div className="dashboard-charts">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Agendamentos por mês</h3>
            <span>Volume de visitas</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.monthlyAppointments} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#b6b6b6" />
              <YAxis stroke="#b6b6b6" allowDecimals={false} />
              <Tooltip cursor={{ stroke: PRIMARY_COLOR }} />
              <Line type="monotone" dataKey="total" stroke={PRIMARY_COLOR} strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3>Faturamento por mês</h3>
            <span>Pagamentos confirmados</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.monthlyRevenue} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" />
              <XAxis dataKey="month" stroke="#b6b6b6" />
              <YAxis stroke="#b6b6b6" tickFormatter={formatCurrency} />
              <Tooltip formatter={(value) => formatCurrency(value)} cursor={{ fill: 'rgba(236,142,0,0.08)' }} />
              <Bar dataKey="total" fill={PRIMARY_COLOR} radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card full">
          <div className="chart-header">
            <h3>Serviços mais realizados</h3>
            <span>Top serviços por volume</span>
          </div>
          <div className="pie-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={charts.serviceBreakdown}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={3}
                >
                  {charts.serviceBreakdown.map((entry, index) => (
                    <Cell key={entry.name} fill={index % 2 === 0 ? PRIMARY_COLOR : '#ffaa2c'} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            {!charts.serviceBreakdown.length && <p className="empty-indicator">Sem dados de serviços confirmados.</p>}
          </div>
        </div>
      </div>

      <div className="dashboard-rankings">
        <div className="ranking-card">
          <div className="ranking-header">
            <div>
              <p className="eyebrow">Ranking 1</p>
              <h3>Clientes que mais visitam</h3>
            </div>
            <span className="pill">Top 10</span>
          </div>
          <div className="table-wrapper">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Total</th>
                  <th>Barbeiro mais atendido</th>
                  <th>Última visita</th>
                </tr>
              </thead>
              <tbody>
                {visitRanking.length ? (
                  visitRanking.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{row.visits}</td>
                      <td>{row.topProfessional}</td>
                      <td>{row.lastVisit}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="empty-indicator">Nenhum agendamento confirmado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="ranking-card">
          <div className="ranking-header">
            <div>
              <p className="eyebrow">Ranking 2</p>
              <h3>Clientes que mais gastam</h3>
            </div>
            <span className="pill">Top 10</span>
          </div>
          <div className="table-wrapper">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Total gasto</th>
                  <th>Média por visita</th>
                  <th>Serviço mais utilizado</th>
                </tr>
              </thead>
              <tbody>
                {spendRanking.length ? (
                  spendRanking.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{formatCurrency(row.totalSpent)}</td>
                      <td>{formatCurrency(row.averageTicket)}</td>
                      <td>{row.topService}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="empty-indicator">Nenhum pagamento confirmado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {loading && <div className="loading-indicator">Carregando dados...</div>}
    </div>
  );
}